/**
 * Orchestrator for the landing page's REALTIME voice demo path.
 *
 * Owns: anonymous token fetch + realtime decision + VoiceStreamingSession
 * lifecycle + partial/final/error callbacks + the batch-fallback signal. It does
 * NOT own the mic — the caller (the demo card) captures PCM and pushes base64
 * frames in via `pushFrame`, so the platform capture seam stays out of here and
 * this module is unit-testable with injected fakes.
 *
 * `relayUrl` comes from the token endpoint and decides which path runs: a URL
 * drives the live streaming session, `null` makes `begin()` return false so the
 * caller runs the batch fallback (`submitAnonymousDemo`). Only production
 * returns `null`, and only when the server advertises no relay at all (neither
 * `VOICE_RELAY_PUBLIC_URL` nor an alt ingress) — that is realtime deliberately
 * switched off, not an error.
 *
 * Error policy (no silent catch): a per-IP cap (429) rethrows as a typed
 * `RealtimeDemoRateLimitedError` so the caller shows the cap notice and stops;
 * every other failure either returns `false` (caller falls back to batch) or is
 * reported through `callbacks.onError` with a visible recovery.
 */

import { reportHandledException } from '@/lib/error-reporting'
import {
  resolveVoiceStreamingDecision,
  VoiceStreamingSession,
  type VoiceStreamingSession as VoiceStreamingSessionType,
} from '@kiao-voice/voice'
import { ApiError } from '@kiao-voice/api-client'
import type { VoiceDemoTokenResponse } from '@kiao-voice/api-contract/voice-demo'
import { createWebPublicApiClient, getWebPublicApiClient } from '../../lib/web-api-client'

export type RealtimeDemoErrorCode = 'rate_limited' | 'no_speech' | 'unavailable'

/** Thrown by `begin()` when the token endpoint reports a per-IP cap (429). */
export class RealtimeDemoRateLimitedError extends Error {
  readonly code = 'rate_limited' as const

  constructor(message = 'Realtime voice demo rate limited.') {
    super(message)
    this.name = 'RealtimeDemoRateLimitedError'
  }
}

export interface RealtimeDemoCallbacks {
  onPartial: (text: string) => void
  /** status:'ok' → the enhanced final text. */
  onFinal: (text: string) => void
  onError: (code: RealtimeDemoErrorCode) => void
}

export interface RealtimeDemoClient {
  /**
   * Fetch the token, build + start the session. Resolves `true` if realtime is
   * live, `false` if the caller must use the batch fallback (relay URL
   * null/disabled, or a non-rate-limit token-fetch failure, or `start()`
   * rejected). Throws ONLY `RealtimeDemoRateLimitedError` (so the caller shows
   * the cap notice and stops).
   */
  begin(): Promise<boolean>
  /** Forward a base64 PCM frame to the live session (no-op before begin/after dispose). */
  pushFrame(base64Pcm: string): void
  /** Send stop, await the relay final, fire onFinal/onError. */
  finish(): Promise<void>
  /** Abort the session + drop refs (unmount / fallback). */
  dispose(): void
}

interface CreateSessionArgs {
  relayUrl: string
  sampleRate: number
  token: string
  language?: string
  onPartial: (text: string) => void
}

export interface CreateRealtimeDemoClientOptions {
  sampleRate: number
  language?: string
  callbacks: RealtimeDemoCallbacks
  // seams for tests:
  fetchFn?: typeof fetch
  createSession?: (args: CreateSessionArgs) => VoiceStreamingSessionType
}

function defaultCreateSession(args: CreateSessionArgs): VoiceStreamingSessionType {
  return new VoiceStreamingSession({
    relayUrl: args.relayUrl,
    sampleRate: args.sampleRate,
    token: args.token,
    language: args.language,
    onPartial: args.onPartial,
  })
}

export function createRealtimeDemoClient(
  opts: CreateRealtimeDemoClientOptions
): RealtimeDemoClient {
  // PUBLIC client (#223): the anonymous demo token endpoint takes no auth and
  // no active context, even if the visitor happens to be signed in.
  const client = opts.fetchFn ? createWebPublicApiClient(opts.fetchFn) : getWebPublicApiClient()
  const createSession = opts.createSession ?? defaultCreateSession
  const { callbacks, sampleRate, language } = opts

  let session: VoiceStreamingSessionType | null = null

  /**
   * Fetch the anonymous demo token. 429 → throw the rate-limited error so the
   * caller stops; any other non-2xx or network error → propagate the thrown
   * ApiError/Error so `begin()`'s catch maps it to the batch fallback (false).
   */
  async function fetchToken(): Promise<VoiceDemoTokenResponse> {
    try {
      return await client.request<VoiceDemoTokenResponse>('/api/v1/voice-demo/token', {
        method: 'POST',
      })
    } catch (error) {
      if (error instanceof ApiError && error.status === 429) {
        throw new RealtimeDemoRateLimitedError()
      }
      throw error
    }
  }

  async function begin(): Promise<boolean> {
    let token: VoiceDemoTokenResponse
    try {
      token = await fetchToken()
    } catch (error) {
      if (error instanceof RealtimeDemoRateLimitedError) {
        throw error
      }
      reportHandledException(error, { source: 'features/landing-demo/realtime-demo-client.ts' })
      // Non-rate-limit failure → caller runs the batch fallback.
      return false
    }

    const decision = resolveVoiceStreamingDecision({
      serverSttMode: 'realtime',
      serverRelayUrl: token.relayUrl,
      envRelayUrl: '',
    })
    if (!decision.enabled || decision.relayUrl === '') {
      return false
    }

    const built = createSession({
      relayUrl: decision.relayUrl,
      sampleRate,
      token: token.token,
      language,
      onPartial: callbacks.onPartial,
    })
    session = built

    try {
      await built.start()
    } catch (caughtError) {
      reportHandledException(caughtError, {
        source: 'features/landing-demo/realtime-demo-client.ts',
      })
      // Relay refused / closed before ready → abort + caller runs the batch
      // fallback on the already-captured WAV.
      built.abort()
      session = null
      return false
    }

    return true
  }

  function pushFrame(base64Pcm: string): void {
    session?.pushFrame(base64Pcm)
  }

  async function finish(): Promise<void> {
    if (!session) {
      callbacks.onError('unavailable')
      return
    }
    try {
      // `formatted` (not the relay's `faithful` default): the landing demo's
      // whole pitch is that a rambling spoken brain-dump comes back organised —
      // a verbatim clean-up would demo a different, much less interesting
      // product. The desktop app resolves this per power profile; here it is a
      // fixed property of the demo.
      const result = await session.stop({
        ...(language ? { language } : {}),
        enhancementMode: 'formatted',
      })
      if (result.status === 'ok' && result.text.trim()) {
        callbacks.onFinal(result.text)
      } else {
        // noise or empty/blank final → no-speech notice, not an empty output.
        callbacks.onError('no_speech')
      }
    } catch (caughtError) {
      reportHandledException(caughtError, {
        source: 'features/landing-demo/realtime-demo-client.ts',
      })
      // Relay error / 30s timeout — report so the caller can run the batch
      // fallback on the captured WAV.
      callbacks.onError('unavailable')
    }
  }

  function dispose(): void {
    session?.abort()
    session = null
  }

  return { begin, pushFrame, finish, dispose }
}
